import { cloneDeep, omit, set } from 'lodash';
import { createCanvas, fixZipResUrl, textBridge } from '@meitu/wasm-lib';
import { getFontFamilySubsetUrl, trackConfig, type IFramework } from '@/framework';
import { base64Kit } from '@/utils/base64-kit';
import { isMd5Uri } from '@/utils/file-kit';
import { mapRange } from '@/utils/math-kit';
import { containsEmoji, TextMeasurer } from '@/utils/text-kit';
import { transformTextDataToLowLever } from '@/utils/transform-kit';
import {
    LayerType,
    MaterialIdsType,
    TextShapeType,
    TextWritingType,
    type ILayerProps,
    type IShapeExtension,
    type ITextLayerProps,
} from '@/version-types';
import { defaultShapeParams, enableAspectRatioShapeTypes, getShapeTypeConfig, shapePlistConfigID } from '../config';
import {
    AR_PUBLIC_PARAMS,
    AR_RES_ID,
    AR_RES_PATH,
    HBP_TEXT_PATH,
    HBP_TEXT_PLIST,
    HBP_TEXT_RES_ID,
    SHAPE_TEXT_PATH,
    SHAPE_TEXT_RES_ID,
    TEXT_LAYER_EVENT,
} from '../constants';
import { type ShapeTextProps } from '../types';
import { getShapeTextPlist, pushHistory, ShapeTypeChecker, updateLayer } from '../utils';

// 底层文本图层参数
type FoundationTextLayerData = Parameters<typeof textBridge.updateTextLayerAttr>[0];

type FoundationWrapTextConfig = ReturnType<typeof textBridge.getTextWarpConfig>;
type FoundationPathTextConfig = ReturnType<typeof textBridge.getTextPathConfig>;

export type UnsetOptions = {
    update?: boolean;
    removeMaterialID?: boolean;
};

export type ShapeTextPropsOptions = {
    props: ShapeTextProps;
    writeHistory?: boolean;
};

export interface IShapeText {
    /**
     * 应用变形文字
     *
     * @param layerID - 图层 ID
     * @param shapeType - 形状类型
     * @param options - 配置选项
     * - `options.materialID` - 素材 ID
     * - `options.params` - 当前调整项配置，设置该参数的原因是因为在应用变形文字时，可能已经存在一些变形文字的配置
     */
    applyShapeText(
        layerID: string,
        shapeType: TextShapeType,
        options?: { materialID?: number; params?: IShapeExtension },
    ): Promise<ILayerProps | undefined>;

    /**
     * 移除变形文字效果
     *
     * @param layerID - 图层 ID
     * @param options - 配置选项
     *
     * - `options.update` - 是否更新舞台，如果为 false 则不更新，直接返回移除变形文字属性后的文本图层
     * - `options.removeMaterialID` - 移除变形效果的同时是否也移除对应的素材 ID
     */
    cancelShapeEffect(layerID: string, options?: UnsetOptions): ILayerProps | undefined;

    /**
     * 设置变形文字参数
     *
     * @param layerID - 图层 ID
     * @param key - 参数名
     * @param value - 参数值
     * @param options - 配置选项
     * - `options.props` - 当前调整项配置
     * - `options.writeHistory` - 是否写入历史记录，比如当调整项为 range 时，需要在 onChange 中实时更新效果，但不需要写入历史记录
     */
    adjustShapeTextProps(
        layerID: string,
        key: string,
        value: number | boolean,
        options: ShapeTextPropsOptions,
    ): Promise<ILayerProps | undefined>;

    /**
     * 根据传入的图层更新对应的截图链接地址
     *
     * @param layer - 图层属性
     */
    refreshLayerScreenshotURL(layer: ILayerProps): Promise<ILayerProps>;

    /**
     * wasm 资源加载
     * @returns
     */
    loadWasmAssets(): Promise<unknown>;

    /**
     * 监听取消变形文字
     *
     * @param fn - 回调函数
     * @returns
     */
    onCancelShapeText(fn: (layer: ILayerProps) => void): () => void;
}

export class ShapeText implements IShapeText {
    /**
     * wasm 资源是否加载完成
     */
    private isAssetsLoaded = false;

    /**
     * emoji 字体是否加载完成
     */
    private isEmojiFontLoaded = false;

    /**
     * 底层字体子集缓存
     * textBridge.registerSubFont 返回的字体 ID
     */
    private foundationSubFontCache = '';

    /**
     * glRender 信息
     * 在开启 GLRender 时，会将图层的宽、高、位置、旋转信息记录在该对象中
     */
    private glRenderInfo = {
        width: 0,
        height: 0,
        left: 0,
        top: 0,
        rotate: 0,
    };

    private prevLayer: ILayerProps | null = null;

    /**
     * 在调节变形文字属性时，是否已经进行过 glRender
     * 若已经进行过 glRender，则不需要重复进行调用，否则会引发性能问题
     */
    private alreadyRegisterGLParams = false;

    /**
     * 不同类型的变形文字可调节参数配置字典
     */
    private static shapeTypeConfig = getShapeTypeConfig();

    constructor(private framework: IFramework) {}

    /**
     * 加载基础字体资源
     * @returns
     */
    private async loadBaseFontAssets() {
        return textBridge.loadTextPathEffectZip(HBP_TEXT_RES_ID, fixZipResUrl(HBP_TEXT_PATH));
    }

    /**
     * 加载 ar 通用资源
     * @returns
     */
    private loadARCommonAssets() {
        return textBridge.loadTextPathEffectZip(AR_RES_ID, fixZipResUrl(AR_RES_PATH));
    }

    /**
     * 加载变形文字资源
     * @returns
     */
    private loadShapeTextAssets() {
        return textBridge.loadTextPathEffectZip(SHAPE_TEXT_RES_ID, fixZipResUrl(SHAPE_TEXT_PATH));
    }

    /**
     * wasm兜底字体，正常不用会用到，但内部流程有调用该zip解压
     */
    private async loadFallbackAssets() {
        return textBridge.registerFont(
            'AlibabaPuHuiTi-Regular',
            (await import('../assets/shape-text/AlibabaPuHuiTi-Regular-subset.zip')).default,
        );
    }

    /**
     * 加载 emoji 字体资源，只有当文本包含 emoji 时才需要加载
     */
    private async loadEmojiAssets() {
        return textBridge.registerFont('emoji', (await import('../assets/shape-text/NotoColorEmoji.ttf.zip')).default);
    }

    /**
     * 注册 ar 通用参数，实现文字渐变
     */
    private async registerARPublicParams() {
        textBridge.registerArPublic(fixZipResUrl(AR_PUBLIC_PARAMS));
    }

    /**
     * 并发资源加载 promise 列表
     */
    private loadAssetsPromiseList: ((value: boolean) => void)[] = [];

    /**
     * 变形文字所需资源加载
     * FIXME: 资源加载失败情况下会发生什么？（错误处理）
     */
    loadWasmAssets() {
        return new Promise((resolve) => {
            if (!this.isAssetsLoaded) {
                this.loadAssetsPromiseList.push(resolve);
            } else {
                resolve(true);
                return;
            }
            if (this.loadAssetsPromiseList.length === 1) {
                const finallyFn = () => {
                    this.isAssetsLoaded = true;
                    this.loadAssetsPromiseList.forEach((fn) => fn(true));
                    this.loadAssetsPromiseList = [];
                };

                Promise.all([
                    this.loadBaseFontAssets(),
                    this.loadARCommonAssets(),
                    this.loadShapeTextAssets(),
                    this.loadFallbackAssets(),
                    this.registerARPublicParams(),
                ])
                    .then(() => {
                        finallyFn();
                    })
                    .catch((err) => {
                        console.log('loadAssets error', err);
                        finallyFn();
                    });
            }
        });
    }

    /**
     * 使用变形文字形式渲染文本图层
     *
     * @param layer - 图层属性
     */
    private getLayerLayoutFromFoundation(layer: ILayerProps) {
        const layerData = layer.data as ITextLayerProps;
        const shapeExtension = layerData.shapeExtension;
        if (!shapeExtension) return;

        const stageIdx = this.framework.stage.getActiveStageIndex();
        const stageWidth = this.framework.getSizeByIndex(stageIdx).width;

        // 将文本图层 layerData 转为底层数据
        const layerWidth = layer.width;
        const layerHeight = layer.height;
        const foundationTextLayerData = transformTextDataToLowLever(
            cloneDeep(layerData),
        ) as unknown as FoundationTextLayerData;

        // 设置底层文本图层参数
        textBridge.updateTextLayerAttr(
            foundationTextLayerData,
            layerWidth,
            layerHeight,
            stageWidth,
            false,
            this.foundationSubFontCache,
        );

        const shapeType = shapeExtension.curveTextType;
        const isWrapEffect = ShapeTypeChecker.isWrapEffect(shapeType);
        const isPathEffect = ShapeTypeChecker.isPathEffect(shapeType);

        // 设置变形plist
        const plistPath = getShapeTextPlist(shapePlistConfigID[shapeType]);
        const plistSetter = isWrapEffect ? 'setTextWarpPlist' : 'setTextPathPlist';
        textBridge[plistSetter](plistPath);

        // 获取底层参数
        const configGetters = isWrapEffect ? 'getTextWarpConfig' : 'getTextPathConfig';
        const shapeEffectConfig = textBridge[configGetters]();

        // 根据变形效果设置底层参数
        // 1. 开启变形效果
        // 2. 根据 shapeType 设置是否开启宽高比
        if (isPathEffect) {
            (shapeEffectConfig as FoundationPathTextConfig).enable = true;
            (shapeEffectConfig as FoundationPathTextConfig).enableAspectRatio = enableAspectRatioShapeTypes[shapeType];
        }

        // 将本地变形文字配置合并至底层参数
        Object.keys(shapeExtension).forEach((key) => {
            const value = Reflect.get(shapeExtension, key);
            if (typeof value !== 'undefined') {
                Reflect.set(shapeEffectConfig, key, value);
            }
        });

        // 底层参数设置
        if (isWrapEffect) {
            textBridge.setTextWarpConfig(shapeEffectConfig as FoundationWrapTextConfig);
        } else {
            textBridge.setTextPathConfig(shapeEffectConfig as FoundationPathTextConfig);
        }

        const glRenderInfo = this.glRenderInfo;
        return textBridge.refreshLayerPosition(
            glRenderInfo.left,
            glRenderInfo.top,
            glRenderInfo.width,
            glRenderInfo.height,
            glRenderInfo.rotate,
        );
    }

    private getFontSubsetURL(layer: ILayerProps) {
        const layerData = layer.data as ITextLayerProps;
        const { text, fontFamily } = layerData;

        if (!text) return '';

        const fonts = this.framework.getFontList();
        const target = fonts.find((font) => font['font_name'] === fontFamily);
        if (!target) return '';

        return getFontFamilySubsetUrl(target['zip_url'], text, 'ttf');
    }

    /**
     * 获取 `textBridge.getTransformResultWithScale` 所需的缩放倍数
     * 主要是为了解决在小尺寸舞台下，文字变形效果失真问题
     */
    private getTransformScale() {
        let scale = 1;

        const stageIdx = this.framework.stage.getActiveStageIndex();
        const stageWidth = this.framework.getSizeByIndex(stageIdx).width;

        const MIN_STAGE_WIDTH = 400;

        if (stageWidth < MIN_STAGE_WIDTH) {
            scale = MIN_STAGE_WIDTH / stageWidth;
        }

        return scale;
    }

    private async getScreenshotURL(layer: ILayerProps) {
        if (!layer) return null;

        const originLayerData = layer.data as ITextLayerProps;
        const layerData = omit(cloneDeep(originLayerData), ['screenshotUrl', 'textContents']);
        const params = JSON.stringify({
            width: layer.width,
            height: layer.height,
            ...layerData,
        });

        const md5 = base64Kit.md5(params);
        const localURL = base64Kit.getLocalUrl(md5);

        if (!isMd5Uri(localURL)) return md5;

        const result = await textBridge.getTransformResultWithScale(this.getTransformScale());
        if (!result.canvas) return null;

        const dataURL = result.canvas.toDataURL();
        return base64Kit.getCode(dataURL, params);
    }

    private async glRender(layer: ILayerProps) {
        if (!this.isAssetsLoaded) {
            await this.loadWasmAssets();
        }

        const { fontFamily, text } = layer.data as ITextLayerProps;
        const hasEmoji = containsEmoji(text);

        if (hasEmoji && !this.isEmojiFontLoaded) {
            await this.loadEmojiAssets();
            this.isEmojiFontLoaded = true;
        }

        this.glRenderInfo.width = layer.width;
        this.glRenderInfo.height = layer.height;
        this.glRenderInfo.left = layer.left;
        this.glRenderInfo.top = layer.top;
        this.glRenderInfo.rotate = layer.rotate;

        const stageIdx = this.framework.stage.getActiveStageIndex();
        const { width, height } = this.framework.getSizeByIndex(stageIdx);
        const canvas = createCanvas(width, height);

        await textBridge
            .from(canvas)
            .apply(
                HBP_TEXT_RES_ID,
                fixZipResUrl(HBP_TEXT_PATH),
                HBP_TEXT_PLIST,
                AR_RES_ID,
                fixZipResUrl(AR_RES_PATH),
                hasEmoji ? ['emoji'] : undefined,
                true,
            );

        const fontSubsetURL = this.getFontSubsetURL(layer);

        this.foundationSubFontCache = await textBridge.registerSubFont(fontFamily, text, fontSubsetURL);

        const layout = this.getLayerLayoutFromFoundation(layer);
        const nextLayer = { ...layer, ...layout };

        return nextLayer;
    }

    async applyShapeText(
        layerID: string,
        shapeType: TextShapeType,
        options: { materialID?: number; params?: IShapeExtension } = {},
    ) {
        const prevLayer = this.framework.getLayer(layerID, true);
        if (!prevLayer || prevLayer.layerType !== LayerType.TEXT_LAYER) return;

        const { materialID, params } = options;

        const prevLayerData = prevLayer.data as ITextLayerProps;
        const exitShapeExtension = prevLayerData.shapeExtension;
        if (exitShapeExtension && exitShapeExtension.curveTextType === shapeType) {
            return;
        }

        const config = ShapeText.shapeTypeConfig[shapeType];
        if (!config) return;

        const shapeExtension: IShapeExtension = params
            ? params
            : {
                  curveTextType: shapeType,
                  ...defaultShapeParams,
              };

        if (!params) {
            for (let i = 0; i < config.length; i++) {
                const item = config[i];
                const { value } = item;

                if (typeof value === 'boolean') {
                    shapeExtension[item.param] = value;
                } else {
                    const { range, ratio } = item;
                    shapeExtension[item.param] = mapRange(value, range, ratio); // 转换为底层值显示
                }
            }
        }

        const nextLayerData: ITextLayerProps = {
            ...prevLayerData,
            shapeExtension,
        };
        let nextLayer: ILayerProps = {
            ...prevLayer,
            data: nextLayerData,
        };

        this.framework.setLoading(layerID);

        try {
            nextLayer = await this.glRender(nextLayer);

            // set screenshot
            const screenshotURL = await this.getScreenshotURL(nextLayer);
            nextLayer = set(nextLayer, 'data.screenshotUrl', screenshotURL);

            // update materialIds
            if (materialID) {
                const materialIds = nextLayer.materialIds || {};
                materialIds[MaterialIdsType.TEXT_TRANSFORM] = [`${materialID}`];
                nextLayer = set(nextLayer, 'materialIds', materialIds);
            }

            // fix: 等待生成变形图片，图层数据可能被其他用户修改，所以需要获取最新的数据写入记录
            // https://jira.meitu.com/browse/MTXXPRO-59635
            const oldLayer = this.framework.getLayer(layerID, true);
            updateLayer(this.framework, nextLayer);
            pushHistory(this.framework, oldLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER].transformEffect);
        } catch (error) {
            console.error('applyShapeText error:', error);
        } finally {
            this.framework.removeLoading(layerID);
        }

        return nextLayer;
    }

    cancelShapeEffect(layerID: string, options: UnsetOptions = {}) {
        const prevLayer = this.framework.getLayer(layerID, true);
        if (!prevLayer || prevLayer.layerType !== LayerType.TEXT_LAYER) return;

        const prevLayerData = prevLayer.data as ITextLayerProps;
        const shapeExtension = prevLayerData.shapeExtension;
        if (!shapeExtension) return;

        const { update = true, removeMaterialID = true } = options;

        const nextLayerData: ITextLayerProps = {
            ...prevLayerData,
            shapeExtension: null,
            screenshotUrl: null,
        };

        const nextLayer: ILayerProps = {
            ...prevLayer,
            data: nextLayerData,
        };

        if (removeMaterialID) {
            const materialIds = nextLayer.materialIds || {};
            materialIds[MaterialIdsType.TEXT_TRANSFORM] = [];
            nextLayer.materialIds = materialIds;
        }

        // 重新根据文本计算图层 layout
        const textSize = TextMeasurer.measure(nextLayer, {
            keepConstantWidth: nextLayerData.writingMode === TextWritingType.HORIZONTAL_TB,
        });
        const center = {
            x: nextLayer.left + nextLayer.width / 2,
            y: nextLayer.top + nextLayer.height / 2,
        };

        nextLayer.width = textSize.width;
        nextLayer.height = textSize.height;
        nextLayer.left = Math.floor(center.x - textSize.width / 2);
        nextLayer.top = Math.floor(center.y - textSize.height / 2);

        if (update) {
            updateLayer(this.framework, nextLayer);
            pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER].transformEffect);

            // 广播取消变形文字
            this.framework.events.emit(TEXT_LAYER_EVENT.CANCEL_SHAPE_TEXT, nextLayer);
        }

        return nextLayer;
    }

    // FIXME: 可改成队列渲染
    //       因为在调整变形文字属性时，只有第一次需要执行 glRender
    //       但是目前的方式可能会执行多次 glRender，且在写入历史阶段很有可能第一次的 glRender 还没有执行完
    //       正确的做法应该是内部维护一个 render buffer
    //       在调节 number 类型（即通过滑块调整数值）时，推送至 render buffer
    //       真正执行调整再不断从 render buffer 中取出渲染函数逐个执行
    async adjustShapeTextProps(layerID: string, key: string, value: number | boolean, options: ShapeTextPropsOptions) {
        const prevLayer = (() => {
            if (this.prevLayer) return this.prevLayer;
            this.prevLayer = this.framework.getLayer(layerID, true);
            return this.prevLayer;
        })();
        if (!prevLayer || prevLayer.layerType !== LayerType.TEXT_LAYER) return;

        const layerData = prevLayer.data as ITextLayerProps;
        const shapeExtension = layerData.shapeExtension;
        if (!shapeExtension) return;

        const { props, writeHistory = true } = options;

        let nextValue = value;
        if (typeof value === 'number' && 'range' in props) {
            const { range, ratio } = props;
            nextValue = mapRange(value, range, ratio); // 转换为底层值显示
        }

        const nextShapeExtension = { ...shapeExtension, [key]: nextValue };
        const nextLayerData: ITextLayerProps = { ...layerData, shapeExtension: nextShapeExtension };
        let nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        nextLayer.__renderGLCanvas = true;

        if (!this.alreadyRegisterGLParams) {
            nextLayer = await this.glRender(nextLayer);
            this.alreadyRegisterGLParams = true;
        } else {
            const layout = this.getLayerLayoutFromFoundation(nextLayer);
            nextLayer = { ...nextLayer, ...layout };
        }

        if (writeHistory) {
            const screenshotURL = await this.getScreenshotURL(nextLayer);
            if (screenshotURL) {
                nextLayer = set(nextLayer, 'data.screenshotUrl', screenshotURL);
            }
            nextLayer.__renderGLCanvas = false;

            updateLayer(this.framework, nextLayer);
            pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER].transformEffect);

            this.prevLayer = null;
            this.alreadyRegisterGLParams = false;
        } else {
            updateLayer(this.framework, nextLayer);
        }

        return nextLayer;
    }

    public async refreshLayerScreenshotURL(layer: ILayerProps) {
        let nextLayer = await this.glRender(layer);
        const screenshotURL = await this.getScreenshotURL(nextLayer);

        const nextLayerData: ITextLayerProps = {
            ...(nextLayer.data as ITextLayerProps),
            screenshotUrl: screenshotURL,
        };

        nextLayer = { ...nextLayer, data: nextLayerData };
        return nextLayer;
    }

    public onCancelShapeText(fn: (layer: ILayerProps) => void) {
        const id = this.framework.events.on(TEXT_LAYER_EVENT.CANCEL_SHAPE_TEXT, fn);
        return () => this.framework.events.off(id);
    }
}
